import { httpMethod } from '../enums/httpMethod'
import type { JsonHttpRequestOptions } from '../interfaces/JsonHttpRequestOptions'
import { useToastStore } from '../stores/toast'
import { i18n } from './i18n'

declare const document: any

export class ValidationError extends Error {
    public errors: Record<string, string[]>

    constructor(message: string, errors: Record<string, string[]>) {
        super(message)
        this.name = 'ValidationError'
        this.errors = errors
    }
}

const jsonHttpRequest = async <T = object>(
    method: httpMethod,
    url: string,
    params: Record<
        string,
        string | string[] | number | boolean | null | undefined
    > = {},
    options: JsonHttpRequestOptions = {}
): Promise<T> => {
    const shouldDisplayToast = options.displayToastOnError ?? true

    return new Promise(async (resolve, reject) => {
        try {
            const fetchOptions: RequestInit = {
                method: method,
                keepalive: options.keepalive,
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN':
                        document
                            .querySelector('meta[name="csrf-token"]')
                            ?.getAttribute('content') || '',
                },
            }

            if (method !== httpMethod.GET) {
                fetchOptions.body = JSON.stringify(params)
            }

            const response = await fetch(url, fetchOptions)

            if (!response.ok) {
                // CSRF token mismatch: session expired, reload to get a fresh token
                if (response.status === 419) {
                    window.location.reload()

                    return
                }

                const errorJson = await response.json()

                if (shouldDisplayToast) {
                    const toastStore = useToastStore()
                    let message: string
                    switch (response.status) {
                        case 422:
                            message = i18n.t('common.error.validation')
                            break
                        default:
                            message = i18n.t('common.error.unexpected')
                            break
                    }
                    toastStore.toastError(message)
                }

                if (response.status === 422 && errorJson.errors) {
                    reject(
                        new ValidationError(
                            errorJson.message
                                || i18n.t('common.error.validation'),
                            errorJson.errors
                        )
                    )

                    return
                }

                reject(
                    new Error(
                        errorJson.message
                            || `Response status: ${response.status}`
                    )
                )

                return
            }

            let json
            switch (response.status) {
                case 204:
                    json = {}
                    break
                default:
                    json = await response.json()
            }

            resolve(json)
        } catch (error) {
            if (shouldDisplayToast) {
                const toastStore = useToastStore()
                toastStore.toastError(i18n.t('common.error.unexpected'))
            }

            reject(error)
        }
    })
}

class Http {
    static getJson = <T = object>(
        url: string,
        options: JsonHttpRequestOptions = {}
    ): Promise<T> => {
        return jsonHttpRequest<T>(httpMethod.GET, url, {}, options)
    }

    static postJson = <T = object>(
        url: string,
        params: Record<
            string,
            string | string[] | number | boolean | null | undefined
        > = {},
        options: JsonHttpRequestOptions = {}
    ): Promise<T> => {
        return jsonHttpRequest<T>(httpMethod.POST, url, params, options)
    }

    static putJson = <T = object>(
        url: string,
        params: Record<
            string,
            string | string[] | number | boolean | null | undefined
        > = {},
        options: JsonHttpRequestOptions = {}
    ): Promise<T> => {
        return jsonHttpRequest<T>(httpMethod.PUT, url, params, options)
    }

    static patchJson = <T = object>(
        url: string,
        params: Record<
            string,
            string | string[] | number | boolean | null | undefined
        > = {},
        options: JsonHttpRequestOptions = {}
    ): Promise<T> => {
        return jsonHttpRequest<T>(httpMethod.PATCH, url, params, options)
    }
}

export default Http
export type { JsonHttpRequestOptions }
